import type { ApiError } from "./client";
import type { Permission } from "./permissions";

/**
 * What each error code means to a person, and where it sends them.
 *
 * The four authorization refusals are deliberately not one "forbidden". They
 * ask the reader to do different things — find an administrator, switch store,
 * hand the record to its owner, wait for the account to be set up — and a
 * single message would answer none of them. That is why the server spends four
 * codes on what is one status.
 *
 * The values are i18n message keys, not text. EP00-S05 owns the catalog these
 * resolve against; until it exists {@link describeApiError} returns the key,
 * which renders as something readable and greppable rather than as a blank.
 */
const MESSAGE_KEYS = {
	// Authorization (EP11-S03).
	permission_denied: "errors.permission_denied",
	scope_denied: "errors.scope_denied",
	section_hidden: "errors.section_hidden",
	section_read_only: "errors.section_read_only",
	not_assignee: "errors.not_assignee",
	no_membership: "errors.no_membership",
	org_suspended: "errors.org_suspended",
	// Authentication.
	unauthenticated: "errors.unauthenticated",
	token_revoked: "errors.token_revoked",
	mfa_required: "errors.mfa_required",
	step_up_required: "errors.step_up_required",
	step_up_code_invalid: "errors.step_up_code_invalid",
	// Everything else the envelope can carry.
	not_found: "errors.not_found",
	invalid_argument: "errors.invalid_argument",
	conflict: "errors.conflict",
	version_conflict: "errors.version_conflict",
	internal: "errors.internal",
} as const satisfies Record<string, string>;

export type ErrorCode = keyof typeof MESSAGE_KEYS;

/**
 * The message key for an error code.
 *
 * An unrecognised code falls back to the generic key rather than throwing: a
 * server that grows a new code should not be able to crash a client that has
 * not been redeployed yet.
 */
export function describeApiError(code: string): string {
	return MESSAGE_KEYS[code as ErrorCode] ?? MESSAGE_KEYS.internal;
}

/** Whether this error is a refusal rather than a failure. */
export function isAuthorizationError(error: ApiError): boolean {
	return error.status === 403;
}

/**
 * The permission the caller was missing, when the server named one.
 *
 * `permission_denied` puts it in `field`, so an administrator can be told
 * exactly which of the 146 strings to grant instead of being asked to guess
 * from a screen name.
 */
export function missingPermission(error: ApiError): Permission | null {
	if (error.code !== "permission_denied" || error.field === null) {
		return null;
	}
	return error.field as Permission;
}
